import { ATTACHMENTS_MAX_TOTAL_BYTES } from '../constants';
import type { AttachedFile, AttachmentIssue } from '../types';

/** Allowed extensions (lower-case) and the MIME types browsers report for them. */
const ALLOWED: Record<string, readonly string[]> = {
  pdf: ['application/pdf'],
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  // Browsers (notably on Windows with Excel installed) report CSV in many ways.
  csv: [
    'text/csv',
    'application/vnd.ms-excel',
    'application/csv',
    'text/comma-separated-values',
    'text/plain',
  ],
};

/** Browsers report these when they do not know the type: the extension decides then. */
const GENERIC_MIME_TYPES = ['', 'application/octet-stream'];

/** Lower-cased extension without the dot, or '' when there is none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Display form of the extension: "PDF", "JPG" (".jpeg" shows as JPG), "FILE" when none. */
export function displayExtension(name: string): string {
  const ext = fileExtension(name);
  if (!ext) return 'FILE';
  return (ext === 'jpeg' ? 'jpg' : ext).toUpperCase();
}

function isDuplicate(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

/** Drops files already attached (same name, size and modification time) and repeats within the batch. */
export function dropDuplicates(existing: readonly File[], incoming: readonly File[]): File[] {
  const known = [...existing];
  const fresh: File[] = [];
  for (const file of incoming) {
    if (known.some((k) => isDuplicate(k, file))) continue;
    known.push(file);
    fresh.push(file);
  }
  return fresh;
}

function typeIssue(file: File): AttachmentIssue | null {
  const allowed = ALLOWED[fileExtension(file.name)];
  if (!allowed) return { code: 'type' };
  const mime = file.type.toLowerCase();
  if (GENERIC_MIME_TYPES.includes(mime)) return null;
  if (!allowed.includes(mime)) return { code: 'mismatch' };
  return null;
}

/**
 * Checks the attached list as a whole. Pure and order-based: files are
 * accepted in the order they were added, and an accepted file counts toward
 * the total. Because it is recomputed from the current list, removing a file
 * naturally re-checks those that were rejected for the total (they may fit now).
 *
 * Returns an issue per file id; ids without an issue are valid.
 */
export function validateAttachments(
  files: readonly AttachedFile[],
  maxTotalBytes: number = ATTACHMENTS_MAX_TOTAL_BYTES,
): Map<string, AttachmentIssue> {
  const issues = new Map<string, AttachmentIssue>();
  let total = 0;

  for (const { id, file } of files) {
    const issue = typeIssue(file);
    if (issue) {
      issues.set(id, issue);
      continue;
    }
    if (file.size > maxTotalBytes) {
      issues.set(id, { code: 'too_large' });
      continue;
    }
    if (total + file.size > maxTotalBytes) {
      issues.set(id, { code: 'total_exceeded', left: maxTotalBytes - total });
      continue;
    }
    total += file.size;
  }
  return issues;
}
